import { State, process } from '@progress/kendo-data-query';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { GridDataStateChangeEvent, GridPagerSettings } from '@progress/kendo-react-grid';
import { FC, useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import poHistoryColumns from 'src/assets/static/receipt_details/poHistoryColumns';
import Grid from 'src/components/GridWrapper';
import Button from 'src/components/design-system/Button';
import useWindowSize from 'src/hooks/useWindowSize';
import usePoHistoryApi, { PoHistory } from 'src/services/usePoHistoryApi';
import ClearFilterButton from './design-system/ClearFilterButton';

const initialHistoryState: State = {
  sort: [
    {
      field: 'actionDate',
      dir: 'asc',
    },
  ],
  take: 20,
  skip: 0,
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};

const defaultProps = {
  showColumnMenu: true,
  sortable: true,
  columns: poHistoryColumns,
  pageable: gridPagerSettings,
  DataItemKey: 'rowId',
  resizable: true,
};

interface PoHistoryWizardProps {
  onClose: VoidFunction;
}

const PoHistoryWizard: FC<PoHistoryWizardProps> = ({ onClose }) => {
  const [searchParams] = useSearchParams();
  const { height } = useWindowSize();
  const poId = searchParams.get('poid') || searchParams.get('poId') || '';
  const [dataState, setHistoryState] = useState<State>(initialHistoryState);
  const { isLoading: isPoHistoryLoading, poHistory } = usePoHistoryApi(poId);

  const onReceiptHistoryDataStateChange = useCallback(
    (e: GridDataStateChangeEvent) => setHistoryState(e.dataState),
    []
  );

  const handlePoHistoryClearFilter = useCallback(() => setHistoryState(initialHistoryState), []);

  const { data: dataSource, total } = useMemo(
    () =>
      process(poHistory || [], dataState) as {
        data: PoHistory;
        total: number;
      },
    [dataState, poHistory]
  );

  const dataValues = dataSource.map((item) => {
    if (typeof item.transactionNotes === 'string' && !isNaN(Number(item.transactionNotes))) {
      return { ...item, transactionNotes: '' };
    }
    return item;
  });
  const isClearFiltersDisabled = useMemo(
    () => !dataState.filter?.filters.length,
    [dataState.filter?.filters.length]
  );

  const GridToolbarChildren = () => (
    <div className="flex justify-between w-full">
      <ClearFilterButton onClick={handlePoHistoryClearFilter} disabled={isClearFiltersDisabled} />
    </div>
  );

  return (
    <>
      <p className="font-bold text-sm ml-3">History</p>
      <div className="mt-1 mb-1 px-1">
        <Grid
          id="receiptHistoryGrid"
          dataSource={dataValues}
          dataState={dataState}
          total={total}
          height={`${height - 220}px`}
          onDataStateChange={onReceiptHistoryDataStateChange}
          gridToolbarChildren={<GridToolbarChildren />}
          loading={isPoHistoryLoading}
          {...defaultProps}
        />
      </div>
      <DialogActionsBar>
        <div className="flex justify-end">
          <Button color="info" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogActionsBar>
    </>
  );
};

export default PoHistoryWizard;
